/**
 * Sprint 102 — DK upload cart.
 *
 * Build for one tournament, send those lineups to it, build again with
 * different rules, send them to the next one. Assigned lineups are frozen
 * server-side, so a rebuild never disturbs a tournament you already filled.
 * One merged CSV at the end carries every contest's Entry IDs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadSimple, PaperPlaneRight, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadDkCsv } from "@/lib/dkDownload";

const when = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return String(iso);
  }
};

export const DkUploadCart = ({ source = "smart" }) => {
  const label = source === "raw" ? "Manual" : "Smart";
  const [cart, setCart] = useState(null);
  const [contestId, setContestId] = useState("");
  const [count, setCount] = useState(20);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setCart(await api.uploadCart());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load the upload cart");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const contests = cart?.contests || [];
  const available = cart?.available?.[source] || 0;
  const selected = useMemo(
    () => contests.find((c) => String(c.contest_id) === String(contestId)) || null,
    [contests, contestId],
  );
  const open = selected ? Math.max(0, selected.entries_reserved - selected.assigned) : 0;

  useEffect(() => {
    if (!contestId && contests.length) setContestId(String(contests[0].contest_id));
  }, [contests, contestId]);

  useEffect(() => {
    if (selected) setCount(Math.min(available || 1, open || 1) || 1);
  }, [contestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      await load();
      toast.success(typeof msg === "function" ? msg(r) : msg);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      await downloadDkCsv(api.uploadCartExportUrl(), "warpaint_dk_upload.csv");
      toast.success("CSV downloaded — upload it to DraftKings as-is");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  if (!cart?.template) return null;

  return (
    <div className="border border-[#FFCC00]/30 bg-black/40 p-2.5 mb-2" data-testid={`dk-cart-${source}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold">
          Tournament-by-tournament upload
        </span>
        <span className="mono text-[9px] text-zinc-500" data-testid={`dk-cart-total-${source}`}>
          {cart.total_assigned}/{cart.total_reserved} entries filled · {available} {label.toLowerCase()} lineups in memory
        </span>
      </div>
      <div className="mono text-[10px] text-zinc-500 mt-1 leading-snug">
        Send this build to one tournament, then rebuild with different rules and send that batch to the next.
        What you assign is frozen here — rebuilding never touches it.
      </div>

      <div className="flex items-end gap-2 mt-2 flex-wrap">
        <label className="flex-1 min-w-[180px]">
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Tournament</span>
          <select value={contestId} onChange={(e) => setContestId(e.target.value)}
            data-testid={`dk-cart-contest-${source}`}
            className="w-full bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-1 mono text-[11px] text-zinc-100">
            {contests.map((c) => (
              <option key={c.contest_id} value={c.contest_id}>
                {c.contest_name} · {c.assigned}/{c.entries_reserved}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Lineups</span>
          <input type="number" min={1} max={Math.max(1, open)} value={count}
            onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
            data-testid={`dk-cart-count-${source}`}
            className="w-20 block bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-1 mono text-[11px] text-right text-zinc-100" />
        </label>
        <button type="button" disabled={busy || !available || !open}
          onClick={() => act(() => api.uploadCartAssign(source, contestId, count),
            (r) => `${r.assigned} lineup${r.assigned === 1 ? "" : "s"} sent to ${r.contest.contest_name}`)}
          data-testid={`dk-cart-assign-${source}`}
          title={!available ? `Build lineups in the ${label} Optimizer first` : open ? "" : "This tournament is already full"}
          className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black font-bold disabled:opacity-40 inline-flex items-center gap-1.5">
          <PaperPlaneRight size={12} weight="bold" /> Send to tournament
        </button>
      </div>
      {selected && (
        <div className="mono text-[9px] text-zinc-600 mt-1">
          {selected.contest_name}: {open} of {selected.entries_reserved} slots still open
        </div>
      )}

      {cart.total_assigned > 0 && (
        <div className="mt-2 space-y-1">
          {contests.filter((c) => c.assigned > 0).map((c) => (
            <div key={c.contest_id} className="flex items-center gap-2 flex-wrap border-b border-zinc-900 pb-1"
              data-testid={`dk-cart-row-${c.contest_id}`}>
              <span className={`mono text-[9px] px-1 py-[1px] border ${c.assigned >= c.entries_reserved
                ? "border-[#39FF14]/60 text-[#39FF14]" : "border-[#FFCC00]/60 text-[#FFCC00]"}`}>
                {c.assigned}/{c.entries_reserved}
              </span>
              <span className="text-[11px] text-zinc-200 truncate flex-1 min-w-[120px]">{c.contest_name}</span>
              <button type="button" disabled={busy} onClick={() => act(() => api.uploadCartRemove(c.contest_id),
                `${c.contest_name} emptied`)}
                data-testid={`dk-cart-remove-${c.contest_id}`}
                className="text-zinc-600 hover:text-[#FF3B30] disabled:opacity-40">
                <X size={12} />
              </button>
              <span className="mono text-[9px] text-zinc-600 basis-full">
                {c.source_label} · {c.rules || "—"} · {when(c.assigned_at)}
              </span>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button type="button" disabled={busy} onClick={download} data-testid={`dk-cart-download-${source}`}
              className="flex-1 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[10px] py-2 inline-flex items-center justify-center gap-1.5">
              <DownloadSimple size={14} weight="bold" /> Download DK upload CSV
            </button>
            <button type="button" disabled={busy}
              onClick={() => { if (confirm("Empty the whole upload cart?")) act(() => api.uploadCartClear(), "Cart emptied"); }}
              data-testid={`dk-cart-clear-${source}`}
              className="mono text-[9px] uppercase tracking-widest px-2 border border-zinc-800 text-zinc-500 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-40 inline-flex items-center gap-1">
              <Trash size={12} /> Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DkUploadCart;
